<script lang="ts" setup>
import {SynchroDirection, type SynchroFormData} from "@/domain/type/synchro.type.ts";
import {computed, type ComputedRef, type ModelRef} from "vue";
import FusionLine from "@/components/Fusion/FusionLine.vue";
import FusionActionLine from "@/components/Fusion/FusionActionLine.vue";
import {RequestEntity, type SynchroRequestFieldList} from "@/domain/type/synchro.api.type.ts";

const props = defineProps<{
  requestFields: SynchroRequestFieldList,
  requestListOrder: number[],
  isChanging: boolean,
  entity: RequestEntity
}>();
const modelValue: ModelRef<SynchroFormData> = defineModel({default: {}})
const emit = defineEmits(['submit'])
const isDisallowed: ComputedRef<boolean> = computed(() => {
  return Object.values(props.requestFields)[0]?.isDisallowed ?? true
})

function selectAllFromSource(src1: boolean) {
  Object.values(props.requestFields).forEach((request) => {
    if (request.isSynchronized || request.isDisallowed) {
      return
    }
    const targetIsReadOnly = src1 ? request.readOnlySrc2 : request.readOnlySrc1
    const direction = src1 ? SynchroDirection.SRC1_TO_SRC2 : SynchroDirection.SRC2_TO_SRC1
    if (!targetIsReadOnly) {
      modelValue.value[request.synchroRequestId] = direction
    }
  })
}

const showAction = computed(() => {
  const orderMatchList = props.requestFields[0] !== undefined
  return !isDisallowed.value && orderMatchList
})

const hasSelection = computed(() => {
  return Object.values(modelValue.value).filter(v => v !== undefined).length > 0
})

</script>

<template>
  <div class="d-flex flex-column">
    <div v-if="entity.isDisallowed()" class="d-flex flex-column align-center justify-center fusionLine">
      <span class="text-grey-darken-1 text-subtitle-1 text-center pa-2">Synchronisation désactivée</span>
    </div>
    <div class="content-with-fixed-bottom">
      <fusion-line
        v-for="(requestField,) in requestFields"
        v-model="modelValue[requestField.synchroRequestId]"
        :entity="entity"
        :request="requestField"/>
    </div>
    <fusion-action-line
      v-if="showAction"
      class="fixed-action-line"
      :request-fields="requestFields"
      @submitSrc1="selectAllFromSource(true)"
      @submitSrc2="selectAllFromSource(false)"
    >
      <template v-slot:actions>
        <v-btn class="ma-auto elevation-2 apply-btn" size="default" color="primary" variant="elevated" text="Appliquer" @click="emit('submit')" :disabled="entity.isReadOnly() || !hasSelection"/>
      </template>
    </fusion-action-line>

  </div>
</template>

<style scoped>
.content-with-fixed-bottom {
  padding-bottom: 100px;
}

.fixed-action-line {
  position: fixed;
  bottom: 16px;
  left: 50%;
  transform: translateX(-50%);
  max-width: fit-content;
  background-color: rgba(255, 255, 255, 0.95);
  backdrop-filter: blur(10px);
  -webkit-backdrop-filter: blur(10px);
  z-index: 1000;
  border-radius: 16px;
  box-shadow: 0 8px 32px rgba(0, 0, 0, 0.12), 0 2px 8px rgba(0, 0, 0, 0.08);
  padding: 12px 20px;
  border: 1px solid rgba(0, 0, 0, 0.08);
  animation: slideUp 0.4s cubic-bezier(0.16, 1, 0.3, 1);
}

@keyframes slideUp {
  from {
    opacity: 0;
    transform: translateX(-50%) translateY(30px);
  }
  to {
    opacity: 1;
    transform: translateX(-50%) translateY(0);
  }
}

@media (max-width: 960px) {
  .fixed-action-line {
    max-width: calc(100% - 32px);
  }
}

@media (max-width: 600px) {
  .fixed-action-line {
    bottom: 0;
    left: 0;
    right: 0;
    transform: none;
    max-width: 100%;
    width: 100%;
    padding: 12px 16px;
    border-radius: 16px 16px 0 0;
    box-shadow: 0 -4px 20px rgba(0, 0, 0, 0.15);
  }

  .content-with-fixed-bottom {
    padding-bottom: 80px;
  }
}
</style>
